// /gym-metrics — gym resource economy dashboard (staff-only).
// Epic #550 Phase 1, issue #562.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PerStatRow {
  stat_id: number;
  day: string;
  energy_consumed: number;
  stat_gain: number;
  xp_generated: number;
  trains: number;
}

interface TotalsRow {
  energy_consumed: number;
  stat_gain: number;
  xp_generated: number;
  trains: number;
}

interface MetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  per_stat: PerStatRow[];
  totals: TotalsRow;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GymMetricsPage() {
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MetricsResponse>({
    queryKey: ['gym-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<MetricsResponse>(`/admin/gym-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load gym metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Gym Metrics"
      description="Resource summary across all gym training. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load gym metrics."
    >
      <p className="text-sm text-ink-low">
        Note: rec-center AP cost per training session is not yet logged (SUB-12).
      </p>

      {data != null && (
        <>
          {/* Totals */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Totals</h2>
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="gym-totals-table">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-ink-mid">Energy consumed</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Stat gain</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">XP generated</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Trains</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-panel-elev">
                    <td className="px-3 py-2 tabular-nums">{data.totals.energy_consumed.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.stat_gain.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.xp_generated.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.trains.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Per stat */}
          {data.per_stat.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Per stat / day</h2>
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="gym-per-stat-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Stat ID</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Energy consumed</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Stat gain</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">XP generated</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Trains</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.per_stat.map((row, idx) => (
                      <tr key={idx} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.stat_id}</td>
                        <td className="px-3 py-2 text-ink-low">{row.day}</td>
                        <td className="px-3 py-2 tabular-nums">{row.energy_consumed.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.stat_gain.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.xp_generated.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.trains.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <p className="text-sm text-ink-low">No per-stat data available.</p>
          )}
        </>
      )}
    </EconomyDashboardLayout>
  );
}
